// src/pages/Books.tsx
import { Container, Typography, Grid, Box, IconButton, Button } from "@mui/material";
import { useState, useEffect } from "react";
import { Add } from "@mui/icons-material";

import BookCard from "../components/BookCard";
import EditDialog from "../components/EditDialog";
import { useEditMode } from "../context/EditModeContext";
import ConfirmDeleteDialog from "./Timeline/ConfirmDeleteDialog";
import ImportJsonDialog from "./Timeline/ImportJsonDialog";
import { useSupabaseTable } from "../hooks/useSupabaseTable";
import booksData from "../data/books.json";

export type BookItem = {
  title: string;
  author: string;
  year: string;
  cover: string;
  link: string;
};

export default function Books() {
  const { isEditMode } = useEditMode();

  const [editing, setEditing] = useState<BookItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BookItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  const { data: supabaseItems, insert, update, remove } = useSupabaseTable<BookItem>("book", { realtime: true });
  const [items, setItems] = useState<BookItem[]>([]);

  useEffect(() => {
    if (supabaseItems) setItems(supabaseItems);
  }, [supabaseItems]);

  const handleSave = async (updated: BookItem) => {
    try {
      if (isNew) {
        await insert(updated);
      } else {
        await update(updated, { title: editing?.title });
      }
      setEditing(null);
      setIsNew(false);
    } catch (err) {
      console.error("Error saving book:", err);
    }
  };

  const handleAdd = () => {
    setEditing({ title: "", author: "", year: "", cover: "", link: "" });
    setIsNew(true);
  };

  const handleDeleteClick = (item: BookItem) => {
    setDeleteTarget(item);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove({ title: deleteTarget.title });
      setDeleteTarget(null);
      setConfirmOpen(false);
    } catch (err) {
      console.error("Error deleting book:", err);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setConfirmOpen(false);
  };

  const importJson = async (jsonData: BookItem[]) => {
    try {
      for (const item of jsonData) {
        await insert(item);
      }
    } catch (err) {
      console.error("Error importing books:", err);
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Books</Typography>
        {isEditMode && (
          <Box>
            <IconButton onClick={handleAdd} color="primary">
              <Add />
            </IconButton>
            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              Import JSON
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {items.map((book, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
            <BookCard
              {...book}
              onEdit={() => setEditing(book)}
              onDelete={() => handleDeleteClick(book)}
            />
          </Grid>
        ))}
      </Grid>

      {editing && (
        <EditDialog
          open={!!editing}
          item={editing}
          onSave={handleSave}
          onClose={() => {
            setEditing(null);
            setIsNew(false);
          }}
        />
      )}

      <ImportJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        onSubmit={importJson}
        exampleJson={JSON.stringify(booksData, null, 2)}
      />

      <ConfirmDeleteDialog<BookItem>
        open={confirmOpen}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        target={deleteTarget}
        getDisplayText={(item) => `${item.title} (${item.year})`}
      />
    </Container>
  );
}
